import {
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_NAME_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useSkillsFrameworksSuggestions } from "PFApp/hooks/activity_form/use_skills_frameworks_suggestions";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { SkillsFrameworkRank } from "PFCore/types";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { SkillsFrameworksSuggestionsParams } from "./use_form_skills_frameworks_suggestions.types";
import { getSuggestionsParams } from "./use_form_skills_frameworks_suggestions.utils";

const SET_STATE_DEBOUCE_MS = 500;

export const useFormSkillsFrameworksSuggestions = (isConfigMatchFilterOn: boolean) => {
  const { subscribe, getValues } = useFormContext();
  const [suggestionsParams, setSuggestionsParams] = useState<SkillsFrameworksSuggestionsParams>(
    getSuggestionsParams(getValues())
  );

  const debouncedSetSuggestionsParams = useDebounce(
    (values) =>
      setSuggestionsParams((prev) => ({
        ...prev,
        ...getSuggestionsParams(values)
      })),
    SET_STATE_DEBOUCE_MS
  );

  useEffect(() => {
    subscribe({
      name: [FIELD_NAME_KEY, FIELD_DESCRIPTION_KEY, FIELD_CUSTOM_FIELDS_KEY],
      formState: { values: true },
      callback: ({ values }) => debouncedSetSuggestionsParams(values)
    });
  }, [subscribe, debouncedSetSuggestionsParams]);

  const {
    skillsFrameworksSuggestions: primarySkillsFrameworksSuggestions,
    isSkillsFrameworksSuggestionsFetching: isPrimarySkillsFrameworksSuggestionsFetching
  } = useSkillsFrameworksSuggestions({
    rank: SkillsFrameworkRank.Primary,
    recommendationTerm: suggestionsParams.recommendationTerm,
    customFields: isConfigMatchFilterOn ? suggestionsParams.customFields : undefined,
    isEnabled: !!suggestionsParams.recommendationTerm
  });

  const {
    skillsFrameworksSuggestions: secondarySkillsFrameworksSuggestions,
    isSkillsFrameworksSuggestionsFetching: isSecondarySkillsFrameworksSuggestionsFetching
  } = useSkillsFrameworksSuggestions({
    rank: SkillsFrameworkRank.Secondary,
    recommendationTerm: suggestionsParams.recommendationTerm,
    customFields: isConfigMatchFilterOn ? suggestionsParams.customFields : undefined,
    isEnabled: !!suggestionsParams.recommendationTerm
  });

  return {
    primarySkillsFrameworksSuggestions,
    secondarySkillsFrameworksSuggestions,
    isSkillsFrameworksSuggestionsFetching:
      isPrimarySkillsFrameworksSuggestionsFetching || isSecondarySkillsFrameworksSuggestionsFetching
  };
};
